import React, { useState, useEffect, useRef } from 'react';
import { searchCities } from '@shared/services/api';

/**
 * Location search with city autocomplete from spotify_city table.
 * Also supports "Use current location" via browser geolocation.
 *
 * Props:
 *   value: string (current city text)
 *   onChange: (city, lat, lng) => void
 *   placeholder: string
 *   className: string (for the container)
 */
export default function LocationSearch({ value = '', onChange, placeholder = 'Search city...', className = '' }) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Sync external value
  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Load top cities on mount (for default suggestions)
  const [defaultCities, setDefaultCities] = useState([]);
  useEffect(() => {
    searchCities('', 20).then(data => setDefaultCities(data.cities || [])).catch(() => {});
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query || query.length < 1) {
      setSuggestions(defaultCities);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchCities(query, 20);
        setSuggestions(data.cities || []);
        setShowSuggestions(true);
      } catch (err) {
        console.error('City search failed:', err);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, defaultCities]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectCity = (city) => {
    const label = `${city.city}${city.state ? `, ${city.state}` : ''}, ${city.country}`;
    setQuery(label);
    setShowSuggestions(false);
    onChange?.(label, city.lat, city.lng);
  };

  const detectLocation = async () => {
    setDetecting(true);
    try {
      let latitude, longitude;
      const isNativeApp = window.Capacitor?.isNativePlatform?.();
      if (isNativeApp) {
        const { Geolocation } = await import('@capacitor/geolocation');
        const perm = await Geolocation.checkPermissions();
        if (perm.location !== 'granted') {
          const req = await Geolocation.requestPermissions();
          if (req.location !== 'granted') {
            setDetecting(false);
            return;
          }
        }
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 10000 });
        latitude = pos.coords.latitude;
        longitude = pos.coords.longitude;
      } else {
        if (!navigator.geolocation) { setDetecting(false); return; }
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: false, timeout: 10000 })
        );
        latitude = pos.coords.latitude;
        longitude = pos.coords.longitude;
      }
      try {
        const res = await fetch(`https://photon.komoot.io/reverse?lon=${longitude}&lat=${latitude}`, {
          headers: { 'User-Agent': 'LoudNeighbors/1.0' },
        });
        const data = await res.json();
        const props = data.features?.[0]?.properties || {};
        const city = props.city || props.town || props.village || props.county || '';
        const state = props.state || '';
        const label = city + (state ? `, ${state}` : '');
        setQuery(label);
        onChange?.(label, latitude, longitude);
      } catch {
        onChange?.(`${latitude.toFixed(2)}, ${longitude.toFixed(2)}`, latitude, longitude);
      }
    } catch (err) {
      console.warn('Location detect failed:', err);
    } finally {
      setDetecting(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!e.target.value) onChange?.('', null, null);
            }}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowSuggestions(true);
              } else if (defaultCities.length > 0) {
                setSuggestions(defaultCities);
                setShowSuggestions(true);
              }
            }}
            placeholder={placeholder}
            className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-white/40"
          />

          {/* Suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-black border border-white/20 rounded-xl overflow-hidden z-50 shadow-lg max-h-64 overflow-y-auto">
              {suggestions.map((city, i) => (
                <button
                  key={`${city.city}-${city.country}-${i}`}
                  onClick={() => selectCity(city)}
                  className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center justify-between"
                >
                  <div>
                    <span className="text-white text-sm">{city.city}</span>
                    {city.state && <span className="text-white/40 text-sm">, {city.state}</span>}
                    <span className="text-white/30 text-xs ml-2">{city.country}</span>
                  </div>
                  {city.max_listeners > 0 && (
                    <span className="text-white/20 text-[10px]">{(city.max_listeners / 1000).toFixed(0)}K</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Current location button */}
        <button
          type="button"
          onClick={detectLocation}
          disabled={detecting}
          className="px-3 bg-white/10 border border-white/20 rounded-xl text-white/60 hover:text-white hover:border-white/40 transition-colors flex-shrink-0"
          title="Use current location"
        >
          {detecting ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
